import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  BUTTON_BASE,
  BUTTON_SHAPES,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  type ButtonShape,
  type ButtonSize,
  type ButtonVariant,
} from "@/components/base/buttonStyles";

export type ButtonProps = {
  children?: ReactNode;
  /** Struktur: gefüllt, mit Rahmen oder nur Text. */
  variant?: ButtonVariant;
  /** Größe: xs | sm | md | lg */
  size?: ButtonSize;
  /** Form: soft (Standard) | pill | circle */
  shape?: ButtonShape;
  /** Volle Breite (z. B. auf Mobil) */
  block?: boolean;
  /** Zusätzliche Farb- und Layout-Klassen der jeweiligen Seite. */
  className?: string;
  /** Interner Link - rendert einen Router-Link. */
  to?: string;
  /** Externer Link - rendert ein <a>. */
  href?: string;
  target?: string;
  rel?: string;
  download?: boolean | string;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  title?: string;
  ariaLabel?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
};

export default function Button({
  children,
  variant = "solid",
  size = "md",
  shape = "soft",
  block = false,
  className = "",
  to,
  href,
  target,
  rel,
  download,
  type = "button",
  disabled = false,
  title,
  ariaLabel,
  onClick,
}: ButtonProps) {
  const classes = [
    BUTTON_BASE,
    BUTTON_VARIANTS[variant],
    BUTTON_SIZES[size],
    BUTTON_SHAPES[shape],
    block ? "w-full sm:w-auto" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (to) {
    return (
      <Link to={to} className={classes} title={title} aria-label={ariaLabel}>
        {children}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        download={download}
        className={classes}
        title={title}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {children}
    </button>
  );
}